import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import apiService from "../services/api";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currencyFormatter = new Intl.NumberFormat("en-GH", {
  style: "currency",
  currency: "GHS",
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

function extractResponseData(response) {
  let value = response;

  if (
    value &&
    typeof value === "object" &&
    value.status === undefined &&
    Object.prototype.hasOwnProperty.call(value, "data")
  ) {
    value = value.data;
  }

  if (
    value &&
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "status") &&
    Object.prototype.hasOwnProperty.call(value, "data")
  ) {
    value = value.data;
  }

  return value;
}

function formatPercentage(value) {
  const amount = Number(value || 0);

  return `${amount > 0 ? "+" : ""}${amount.toFixed(2)}%`;
}

function ComparisonCard({
  title,
  periodAValue,
  periodBValue,
  change,
  formatter = numberFormatter.format,
  periodALabel,
  periodBLabel,
}) {
  const numericChange = Number(change || 0);

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body>
        <div className="text-muted small mb-2">{title}</div>

        <Row>
          <Col>
            <div className="small text-muted">{periodALabel}</div>

            <div className="fs-5 fw-semibold">
              {formatter(Number(periodAValue || 0))}
            </div>
          </Col>

          <Col>
            <div className="small text-muted">{periodBLabel}</div>

            <div className="fs-5 fw-semibold">
              {formatter(Number(periodBValue || 0))}
            </div>
          </Col>
        </Row>

        <hr />

        <div
          className={
            numericChange > 0
              ? "text-success fw-semibold"
              : numericChange < 0
                ? "text-danger fw-semibold"
                : "text-muted fw-semibold"
          }
        >
          {formatPercentage(numericChange)}
        </div>

        <div className="small text-muted">Change from first period</div>
      </Card.Body>
    </Card>
  );
}

function MonthComparison() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [filters, setFilters] = useState({
    year_a: currentYear - 1,
    month_a: currentMonth,
    year_b: currentYear,
    month_b: currentMonth,
  });

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const years = useMemo(() => {
    const values = [];

    for (let year = currentYear; year >= currentYear - 10; year -= 1) {
      values.push(year);
    }

    return values;
  }, [currentYear]);

  const loadComparison = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiService.getMonthComparison(filters);

      const data = extractResponseData(response);

      if (!data?.period_a || !data?.period_b) {
        throw new Error("The server returned an invalid comparison response.");
      }

      setComparison(data);
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        requestError?.message ||
        "The comparison could not be loaded.";

      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadComparison();
  }, []); // Initial page load only.

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((current) => ({
      ...current,
      [name]: Number(value),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    loadComparison();
  };

  const chartData = useMemo(() => {
    if (!comparison) {
      return [];
    }

    const periodAMap = new Map(
      comparison.period_a.daily_trend.map((row) => [Number(row.day), row]),
    );

    const periodBMap = new Map(
      comparison.period_b.daily_trend.map((row) => [Number(row.day), row]),
    );

    const allDays = [...periodAMap.keys(), ...periodBMap.keys()];

    const maximumDay = allDays.length > 0 ? Math.max(...allDays) : 31;

    return Array.from({ length: maximumDay }, (_, index) => {
      const day = index + 1;

      return {
        day,
        periodARevenue: Number(periodAMap.get(day)?.revenue || 0),
        periodBRevenue: Number(periodBMap.get(day)?.revenue || 0),
        periodAInvoices: Number(periodAMap.get(day)?.invoices || 0),
        periodBInvoices: Number(periodBMap.get(day)?.invoices || 0),
      };
    });
  }, [comparison]);

  const productRows = useMemo(() => {
    if (!comparison) {
      return [];
    }

    const periodAMap = new Map(
      comparison.period_a.top_products.map((row) => [String(row.product), row]),
    );

    const periodBMap = new Map(
      comparison.period_b.top_products.map((row) => [String(row.product), row]),
    );

    const productNames = new Set([...periodAMap.keys(), ...periodBMap.keys()]);

    return Array.from(productNames)
      .map((product) => ({
        product,
        periodARevenue: Number(periodAMap.get(product)?.revenue || 0),
        periodBRevenue: Number(periodBMap.get(product)?.revenue || 0),
        periodAQuantity: Number(periodAMap.get(product)?.quantity || 0),
        periodBQuantity: Number(periodBMap.get(product)?.quantity || 0),
      }))
      .sort((first, second) => second.periodBRevenue - first.periodBRevenue);
  }, [comparison]);

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h2 className="mb-1">Month-to-Month Comparison</h2>

        <p className="text-muted mb-0">
          Compare sales performance across different months and years.
        </p>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3 align-items-end">
              <Col md={2}>
                <Form.Label>First month</Form.Label>

                <Form.Select
                  name="month_a"
                  value={filters.month_a}
                  onChange={handleFilterChange}
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Label>First year</Form.Label>

                <Form.Select
                  name="year_a"
                  value={filters.year_a}
                  onChange={handleFilterChange}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Label>Second month</Form.Label>

                <Form.Select
                  name="month_b"
                  value={filters.month_b}
                  onChange={handleFilterChange}
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={2}>
                <Form.Label>Second year</Form.Label>

                <Form.Select
                  name="year_b"
                  value={filters.year_b}
                  onChange={handleFilterChange}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={2}>
                <Button type="submit" className="w-100" disabled={loading}>
                  {loading ? "Comparing..." : "Compare periods"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && !comparison && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {comparison && (
        <>
          <Row className="g-3 mb-4">
            <Col md={6} xl={3}>
              <ComparisonCard
                title="Total revenue"
                periodAValue={comparison.period_a.summary.revenue}
                periodBValue={comparison.period_b.summary.revenue}
                change={comparison.change.revenue_percentage}
                formatter={currencyFormatter.format}
                periodALabel={comparison.period_a.label}
                periodBLabel={comparison.period_b.label}
              />
            </Col>

            <Col md={6} xl={3}>
              <ComparisonCard
                title="Invoices"
                periodAValue={comparison.period_a.summary.invoices}
                periodBValue={comparison.period_b.summary.invoices}
                change={comparison.change.invoice_percentage}
                periodALabel={comparison.period_a.label}
                periodBLabel={comparison.period_b.label}
              />
            </Col>

            <Col md={6} xl={3}>
              <ComparisonCard
                title="Quantity sold"
                periodAValue={comparison.period_a.summary.quantity}
                periodBValue={comparison.period_b.summary.quantity}
                change={comparison.change.quantity_percentage}
                periodALabel={comparison.period_a.label}
                periodBLabel={comparison.period_b.label}
              />
            </Col>

            <Col md={6} xl={3}>
              <ComparisonCard
                title="Average order value"
                periodAValue={comparison.period_a.summary.average_order_value}
                periodBValue={comparison.period_b.summary.average_order_value}
                change={comparison.change.average_order_value_percentage}
                formatter={currencyFormatter.format}
                periodALabel={comparison.period_a.label}
                periodBLabel={comparison.period_b.label}
              />
            </Col>
          </Row>

          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h5 className="mb-3">Daily revenue comparison</h5>

              <div style={{ height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                      dataKey="day"
                      label={{
                        value: "Day of month",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />

                    <YAxis />

                    <Tooltip
                      formatter={(value) =>
                        currencyFormatter.format(Number(value || 0))
                      }
                    />

                    <Legend />

                    <Line
                      type="monotone"
                      dataKey="periodARevenue"
                      name={comparison.period_a.label}
                      stroke="#6c757d"
                      strokeWidth={2}
                      dot={false}
                    />

                    <Line
                      type="monotone"
                      dataKey="periodBRevenue"
                      name={comparison.period_b.label}
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Product comparison</h5>

              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>{comparison.period_a.label} revenue</th>
                      <th>{comparison.period_b.label} revenue</th>
                      <th>{comparison.period_a.label} quantity</th>
                      <th>{comparison.period_b.label} quantity</th>
                    </tr>
                  </thead>

                  <tbody>
                    {productRows.map((row) => (
                      <tr key={row.product}>
                        <td>{row.product}</td>

                        <td>{currencyFormatter.format(row.periodARevenue)}</td>

                        <td>{currencyFormatter.format(row.periodBRevenue)}</td>

                        <td>{numberFormatter.format(row.periodAQuantity)}</td>

                        <td>{numberFormatter.format(row.periodBQuantity)}</td>
                      </tr>
                    ))}

                    {productRows.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          No product comparison data is available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}

export default MonthComparison;
